import { AlertCircle, ArrowLeft, CheckCircle2, Handshake, Plus, RotateCcw, X } from "lucide-react";
import { useMutation } from "convex/react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, Label, PageHeader, Section, Tag } from "../components/ui";
import { convexApi } from "../convexApi";
import { buildBalanceOverview } from "../lib/balance";
import { governanceContext } from "../lib/governanceDemo";
import type { LexData } from "../lib/types";
import { delegationVoteCap, type DelegationRecord } from "../lib/voting";

type DelegationPersistence = {
  createDelegation?: (input: { delegateFirmId: string; scopeType: DelegationRecord["scopeType"]; scopeId: string | null; startsAt: number; expiresAt: number }) => Promise<unknown>;
  revokeDelegation?: (delegationId: string) => Promise<unknown>;
};

export function DelegationsPage({ data, mode = "demo" }: { data: LexData; mode?: "convex" | "demo" }) {
  if (mode === "convex") return <LiveDelegationsPage data={data} />;
  return <DelegationsPageContent data={data} />;
}

function LiveDelegationsPage({ data }: { data: LexData }) {
  const createDelegationMutation = useMutation(convexApi.voting.createDelegation);
  const revokeDelegationMutation = useMutation(convexApi.voting.revokeDelegation);
  const persistence: DelegationPersistence = {
    createDelegation: (input) => createDelegationMutation({ delegateFirmId: input.delegateFirmId as never, scopeType: input.scopeType, scopeId: input.scopeId, startsAt: input.startsAt, expiresAt: input.expiresAt }),
    revokeDelegation: (delegationId) => revokeDelegationMutation({ delegationId: delegationId as never }),
  };
  return <DelegationsPageContent data={data} persistence={persistence} />;
}

function DelegationsPageContent({ data, persistence }: { data: LexData; persistence?: DelegationPersistence }) {
  const ctx = useMemo(() => governanceContext(data, data.proposals[0]?.id ?? "prop-1"), [data]);
  const [localDelegations, setLocalDelegations] = useState<DelegationRecord[]>([]);
  const [created, setCreated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [delegateFirmId, setDelegateFirmId] = useState("");
  const [scopeType, setScopeType] = useState<DelegationRecord["scopeType"]>("standing");
  const [scopeReference, setScopeReference] = useState("");
  const [durationDays, setDurationDays] = useState(14);
  const delegations = [...(ctx?.delegations ?? []), ...localDelegations];
  const viewerFirmId = data.viewerFirm?.id;
  const granted = delegations.filter((item) => item.delegatingFirmId === viewerFirmId);
  const received = delegations.filter((item) => item.delegateFirmId === viewerFirmId);
  const capped = delegations.filter((item) => item.status === "inactive_for_cap");
  const availableDelegates = data.firms.filter((firm) => firm.status === "active" && firm.membershipCategory === "Full Member" && firm.id !== viewerFirmId);
  const proposalOptions = data.proposals
    .map((proposal) => {
      const discussion = data.discussions.find((item) => item.id === proposal.discussionId);
      return { id: proposal.id, label: discussion?.title ?? "Governance proposal" };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  const balance = useMemo(() => buildBalanceOverview(data), [data]);
  const activeFullMemberCount = balance.activeFullMemberCount || (ctx?.activeFirmCount ?? 0);
  const holdingCap = delegationVoteCap(activeFullMemberCount);
  const capProjection = useMemo(() => {
    if (!delegateFirmId) return null;
    const activeHeld = delegations.filter((item) => item.delegateFirmId === delegateFirmId && item.status === "active").length;
    const remainingSlots = Math.max(0, holdingCap - 1 - activeHeld);
    const delegateShare = balance.byFirm.find((item) => item.id === delegateFirmId);
    const projectedWeight = (delegateShare?.controlledWeight ?? 0) + (balance.byFirm.find((item) => item.id === viewerFirmId)?.ownWeight ?? 1);
    const projectedShare = balance.totalWeight > 0 ? projectedWeight / balance.totalWeight : 0;
    return {
      delegateName: data.firms.find((firm) => firm.id === delegateFirmId)?.name ?? "the delegate",
      activeHeld,
      remainingSlots,
      wouldBeCapped: remainingSlots <= 0,
      projectedShare,
      overCap: projectedShare > balance.firmCapShare,
    };
  }, [delegateFirmId, delegations, holdingCap, balance, viewerFirmId, data.firms]);

  const openForm = () => {
    setError(null);
    setCreated(false);
    setDelegateFirmId((current) => current || availableDelegates[0]?.id || "");
    setScopeReference((current) => current || proposalOptions[0]?.id || "");
    setFormOpen(true);
  };

  const createDelegation = async () => {
    if (!viewerFirmId) {
      setError("Only a user attached to a firm can give a procuration.");
      return;
    }
    const delegate = availableDelegates.find((firm) => firm.id === delegateFirmId);
    if (!delegate) {
      setError("Choose the member firm that will receive the procuration.");
      return;
    }
    if (scopeType === "vote_specific" && !scopeReference) {
      setError("Choose the proposal this procuration applies to.");
      return;
    }
    setError(null);
    const now = Date.now();
    const validUntil = now + Math.max(1, durationDays) * 24 * 60 * 60 * 1000;
    const savedScopeReference = scopeType === "standing" ? null : scopeReference || null;
    const draft: DelegationRecord = {
      id: `local-delegation-${now}`,
      delegatingFirmId: viewerFirmId,
      delegateFirmId: delegate.id,
      scopeType,
      scopeReference: savedScopeReference,
      validFrom: now,
      validUntil,
      status: "active",
      createdAt: now,
    };
    setLocalDelegations((current) => [...current, draft]);
    try {
      const persistedId = await persistence?.createDelegation?.({ delegateFirmId: delegate.id, scopeType, scopeId: savedScopeReference, startsAt: now, expiresAt: validUntil });
      if (typeof persistedId === "string") {
        setLocalDelegations((current) => current.map((item) => (item.id === draft.id ? { ...item, id: persistedId } : item)));
      }
      setCreated(true);
      setFormOpen(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The procuration could not be saved.");
      setLocalDelegations((current) => current.filter((item) => item.id !== draft.id));
    }
  };

  const revokeDelegation = async (delegationId: string) => {
    const previous = localDelegations;
    setError(null);
    setLocalDelegations((current) => current.map((item) => (item.id === delegationId ? { ...item, status: "revoked", revokedAt: Date.now() } : item)));
    try {
      if (!delegationId.startsWith("local")) await persistence?.revokeDelegation?.(delegationId);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The procuration could not be revoked.");
      setLocalDelegations(previous);
    }
  };

  return (
    <div className="space-y-6">
      <Link to="/my-workspace" className="inline-flex min-h-10 items-center gap-2 rounded-lg text-sm font-medium text-secondary hover:text-ink">
        <ArrowLeft className="h-4 w-4" />
        Back to my workspace
      </Link>

      <PageHeader
        title="Procurations"
        subtitle="Give, receive and monitor delegated voting authority for member-firm decisions."
        actions={
          <Button type="button" onClick={openForm}>
            <span className="inline-flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Give procuration
            </span>
          </Button>
        }
      />

      {formOpen ? (
        <Card className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-semibold text-ink">Give procuration</h2>
              <p className="mt-1 text-sm text-secondary">Choose the member firm, scope and expiry date for the delegated voting authority.</p>
            </div>
            <button type="button" className="grid h-9 w-9 place-items-center rounded-lg hover:bg-hover" onClick={() => setFormOpen(false)} aria-label="Close procuration form">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <label className="grid gap-1.5 text-sm font-medium text-ink">
              Receiving firm
              <select className="min-h-11 rounded-lg border border-line bg-surface px-3 font-normal" value={delegateFirmId} onChange={(event) => setDelegateFirmId(event.target.value)} disabled={!viewerFirmId || availableDelegates.length === 0}>
                {availableDelegates.length > 0 ? availableDelegates.map((firm) => <option key={firm.id} value={firm.id}>{firm.name}</option>) : <option value="">No eligible receiving firm</option>}
              </select>
            </label>

            <label className="grid gap-1.5 text-sm font-medium text-ink">
              Scope
              <select className="min-h-11 rounded-lg border border-line bg-surface px-3 font-normal" value={scopeType} onChange={(event) => setScopeType(event.target.value as DelegationRecord["scopeType"])}>
                <option value="standing">Standing procuration</option>
                <option value="vote_specific">Specific proposal</option>
                <option value="topic_specific">Topic-specific</option>
              </select>
            </label>

            {scopeType === "vote_specific" ? (
              <label className="grid gap-1.5 text-sm font-medium text-ink">
                Proposal
                <select className="min-h-11 rounded-lg border border-line bg-surface px-3 font-normal" value={scopeReference} onChange={(event) => setScopeReference(event.target.value)} disabled={proposalOptions.length === 0}>
                  {proposalOptions.length > 0 ? proposalOptions.map((proposal) => <option key={proposal.id} value={proposal.id}>{proposal.label}</option>) : <option value="">No proposal available</option>}
                </select>
              </label>
            ) : null}

            {scopeType === "topic_specific" ? (
              <label className="grid gap-1.5 text-sm font-medium text-ink">
                Topic
                <input className="min-h-11 rounded-lg border border-line bg-surface px-3 font-normal" value={scopeReference} onChange={(event) => setScopeReference(event.target.value)} placeholder="Example: EU affairs" />
              </label>
            ) : null}

            <label className="grid gap-1.5 text-sm font-medium text-ink">
              Validity
              <select className="min-h-11 rounded-lg border border-line bg-surface px-3 font-normal" value={durationDays} onChange={(event) => setDurationDays(Number(event.target.value))}>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
              </select>
            </label>
          </div>

          {!viewerFirmId ? <p className="mt-3 text-sm text-secondary">Only users attached to a member firm can give procurations.</p> : null}
          {viewerFirmId && availableDelegates.length === 0 ? <p className="mt-3 text-sm text-secondary">There is no other active full member firm available to receive a procuration.</p> : null}
          {capProjection && (capProjection.wouldBeCapped || capProjection.overCap) ? (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {capProjection.wouldBeCapped
                  ? `${capProjection.delegateName} already holds the maximum ${holdingCap - 1} procuration${holdingCap - 1 === 1 ? "" : "s"} for a network of ${activeFullMemberCount} members. A new procuration will be recorded but stays inactive for the cap until a slot frees up.`
                  : `This would put ${capProjection.delegateName} near ${(capProjection.projectedShare * 100).toFixed(0)}% of voting weight, above the ${(balance.firmCapShare * 100).toFixed(0)}% holding cap. Consider spreading procurations across more firms to keep the network balanced.`}
              </span>
            </div>
          ) : capProjection ? (
            <p className="mt-3 text-[12px] text-muted">{capProjection.delegateName} holds {capProjection.activeHeld} of {holdingCap - 1} available procuration slots. {capProjection.remainingSlots} remaining.</p>
          ) : null}

          <div className="mt-5 flex flex-wrap gap-2">
            <Button type="button" onClick={() => void createDelegation()} disabled={!viewerFirmId || availableDelegates.length === 0}>
              Save procuration
            </Button>
            <Button type="button" variant="secondary" onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
          </div>
        </Card>
      ) : null}

      {created ? <div className="rounded-xl border border-line bg-surface px-4 py-3 text-sm text-secondary">The procuration has been created.</div> : null}
      {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">{error}</div> : null}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="grid gap-5 lg:grid-cols-2">
          <DelegationSection title="Granted by my firm" items={granted} data={data} canRevoke onRevoke={(id) => void revokeDelegation(id)} />
          <DelegationSection title="Received by my firm" items={received} data={data} onRevoke={(id) => void revokeDelegation(id)} />
        </div>

        <aside className="space-y-5">
          <Card className="p-5">
            <div className="flex items-center gap-2">
              <Handshake className="h-4 w-4 text-primary" />
              <h2 className="font-semibold text-ink">Delegation cap</h2>
            </div>
            <p className="mt-2 text-sm leading-6 text-secondary">The cap applies to total voting authority: own vote plus delegated votes. Excess delegations stay visible but are not counted, and they no longer change vote outcomes once over the cap.</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-divider bg-canvas p-3">
                <Label>Active full members</Label>
                <div className="mt-1 text-2xl font-semibold text-ink">{activeFullMemberCount}</div>
              </div>
              <div className="rounded-lg border border-divider bg-canvas p-3">
                <Label>Max per delegate</Label>
                <div className="mt-1 text-2xl font-semibold text-ink">{Math.max(0, holdingCap - 1)}</div>
              </div>
            </div>
            <p className="mt-3 text-[12px] text-muted">Single-firm holding cap: {(balance.firmCapShare * 100).toFixed(0)}% of total voting weight.</p>
          </Card>

          <Card className="p-5">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-accent" />
              <h2 className="font-semibold text-ink">Warnings</h2>
            </div>
            {capped.length > 0 ? (
              <div className="mt-3 grid gap-2">
                {capped.map((delegation) => (
                  <div key={delegation.id} className="rounded-lg border border-line bg-canvas p-3 text-sm text-secondary">
                    {firmName(data, delegation.delegatingFirmId)} to {firmName(data, delegation.delegateFirmId)} cannot currently be exercised because the cap is reached.
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-sm text-secondary">No cap warnings right now.</p>
            )}
          </Card>
        </aside>
      </div>
    </div>
  );
}

function DelegationSection({ title, items, data, canRevoke = false, onRevoke }: { title: string; items: DelegationRecord[]; data: LexData; canRevoke?: boolean; onRevoke: (id: string) => void }) {
  return (
    <Section title={title}>
      {items.length > 0 ? (
        <div className="grid gap-3">
          {items.map((item) => (
            <Card key={item.id} className="p-4">
              <div className="flex items-start gap-3">
                {item.status === "active" ? <CheckCircle2 className="mt-1 h-5 w-5 text-primary" /> : <AlertCircle className="mt-1 h-5 w-5 text-accent" />}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-ink">{firmName(data, item.delegatingFirmId)} to {firmName(data, item.delegateFirmId)}</div>
                  <div className="mt-1 text-sm text-secondary">{item.scopeType.replace(/_/g, " ")} · expires {new Date(item.validUntil).toLocaleDateString()}</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Tag>{item.status.replace(/_/g, " ")}</Tag>
                    {item.scopeReference ? <Tag>{item.scopeReference}</Tag> : null}
                  </div>
                </div>
                {canRevoke && item.status === "active" ? (
                  <button type="button" className="grid h-10 w-10 place-items-center rounded-lg hover:bg-hover" onClick={() => onRevoke(item.id)} aria-label="Revoke procuration">
                    <RotateCcw className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-4 text-sm text-secondary">No procurations in this group.</Card>
      )}
    </Section>
  );
}

function firmName(data: LexData, firmId: string) {
  return data.firms.find((firm) => firm.id === firmId)?.name ?? firmId;
}
